import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, X } from "lucide-react";
import { api } from "../../../lib/api";

// Pindah unit ke program periode lain (brief 22 Sep A8, hal. 5–6 & 9):
// dropdown pertama = halaman sejenis (ZOA GROUP / Dealer Partner), dropdown
// kedua = program periode. Opsi yang TIDAK bisa dipilih tetap tampil beserta
// alasannya — keputusannya dari backend (`GET /cashback/pp/opsi-pindah`, satu
// aturan dengan POST pindah), layar tidak menilai sendiri.
export default function PindahUnitModal({ anggotaIds, cbBase, onClose, onDipindah }) {
  const [opsi, setOpsi] = useState(null);
  const [galat, setGalat] = useState(null);
  const [kanal, setKanal] = useState("");
  const [programId, setProgramId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const kunciIds = anggotaIds.join(",");

  useEffect(() => {
    let batal = false;
    api.get(`${cbBase}/pp/opsi-pindah`, { params: { anggota_ids: kunciIds } })
      .then((r) => {
        if (batal) return;
        setOpsi(r.data);
        const h = r.data.halaman || [];
        const pertama = h.find((x) => x.program.some((p) => p.bisa_dipilih)) || h[0];
        setKanal(pertama ? pertama.kanal : "");
      })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.detail || "Gagal memuat pilihan program"); });
    return () => { batal = true; };
  }, [cbBase, kunciIds]);

  const halaman = useMemo(() => (opsi?.halaman || []).find((h) => h.kanal === kanal), [opsi, kanal]);
  const dipilih = useMemo(() => (halaman?.program || []).find((p) => p.id === programId), [halaman, programId]);

  const simpan = async () => {
    if (!dipilih || !dipilih.bisa_dipilih) return;
    setBusy(true);
    try {
      const r = await api.post(`${cbBase}/pp/anggota/pindah`, {
        anggota_ids: anggotaIds, program_tujuan_id: dipilih.id, alasan: catatan.trim(),
      });
      toast.success(r.data.message);
      onDipindah?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindahkan unit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cb-pindah-modal"
           className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-emerald-600" /> Pindahkan {anggotaIds.length} Unit
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3 text-sm">
          {galat && <p className="text-rose-700" data-testid="cb-pindah-galat">{galat}</p>}
          {!opsi && !galat && <p className="text-slate-500">Memuat pilihan program…</p>}
          {opsi && (
            <>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Pindahkan ke</span>
                <select value={kanal} data-testid="cb-pindah-halaman"
                        onChange={(e) => { setKanal(e.target.value); setProgramId(""); }}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2">
                  {opsi.halaman.map((h) => <option key={h.kanal} value={h.kanal}>{h.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Program periode</span>
                <select value={programId} data-testid="cb-pindah-program" onChange={(e) => setProgramId(e.target.value)}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2">
                  <option value="">— pilih program —</option>
                  {(halaman?.program || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} · {p.code}{p.bisa_dipilih ? "" : " (tidak bisa dipilih)"}
                    </option>
                  ))}
                </select>
              </label>
              {halaman && halaman.program.length === 0 && (
                <p className="text-xs text-slate-500">Belum ada program aktif di halaman ini.</p>
              )}
              {dipilih && !dipilih.bisa_dipilih && (
                <div data-testid="cb-pindah-alasan" className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-800 space-y-0.5">
                  <p className="font-medium">Program ini tidak bisa dipilih:</p>
                  {dipilih.alasan.map((a, i) => <p key={i}>• {a}</p>)}
                </div>
              )}
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Catatan (opsional)</span>
                <input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="cb-pindah-catatan"
                       placeholder="mis. Dealer retur, unit dijual ulang di toko"
                       className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2" />
              </label>
            </>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
          <button onClick={simpan} disabled={busy || !dipilih || !dipilih.bisa_dipilih} data-testid="cb-pindah-simpan"
                  className="h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 bg-emerald-600 hover:bg-emerald-700">
            {busy ? "Memproses..." : "Pindahkan"}
          </button>
        </div>
      </div>
    </div>
  );
}
